import React from "react";
import styled from "styled-components";
import { spacingTokens, borderRadiusTokens } from "../../config/designTokens";
import { GBSkeleton, GBSkeletonListItem } from "@greenbackapp/ui";

// ============================================
// Primitive re-exports for direct use
// ============================================

// Named convenience wrappers around GBSkeleton for text/circle/box primitives
export const SkeletonText: React.FC<{ $width?: string; $height?: string; style?: React.CSSProperties; 'data-testid'?: string }> = ({
    $width,
    $height,
    style,
    'data-testid': testID,
}) => (
    // @ts-ignore — style passthrough for web callers
    <GBSkeleton
        type="text"
        width={$width}
        height={$height ? parseInt($height) : undefined}
        style={style}
        testID={testID}
    />
);

export const SkeletonCircle: React.FC<{ $width?: string; $height?: string; style?: React.CSSProperties; 'data-testid'?: string }> = ({
    $width = "40px",
    $height = "40px",
    style,
    'data-testid': testID,
}) => (
    // @ts-ignore — style passthrough for web callers
    <GBSkeleton
        type="avatar"
        width={$width}
        height={$height ? parseInt($height) : undefined}
        style={{ borderRadius: '50%', ...style }}
        testID={testID}
    />
);

export const SkeletonBox: React.FC<{ $width?: string; $height?: string; $borderRadius?: string; style?: React.CSSProperties; 'data-testid'?: string }> = ({
    $width,
    $height,
    $borderRadius,
    style,
    'data-testid': testID,
}) => (
    // @ts-ignore — style passthrough for web callers
    <GBSkeleton
        type="card"
        width={$width}
        height={$height ? parseInt($height) : undefined}
        borderRadius={$borderRadius ? parseInt($borderRadius) : undefined}
        style={{ height: $height, ...style }}
        testID={testID}
    />
);

// ============================================
// Composite Skeletons
// ============================================

interface SkeletonCardProps {
    lines?: number;
    hasIcon?: boolean;
}

const CardSkeletonContainer = styled.div`
    background-color: ${props => props.theme.background.paper};
    border: 1px solid ${props => props.theme.border.default};
    border-radius: ${borderRadiusTokens.md};
    padding: ${spacingTokens.lg};
    display: flex;
    flex-direction: column;
    gap: ${spacingTokens.md};
`;

export const SkeletonCard: React.FC<SkeletonCardProps> = ({ lines = 3, hasIcon = false }) => (
    <CardSkeletonContainer data-testid="skeleton-card">
        {hasIcon && <GBSkeleton type="avatar" width={48} height={48} />}
        <GBSkeleton
            type="pattern"
            pattern={Array.from({ length: lines }).map((_, i) => ({
                type: 'text' as const,
                width: i === lines - 1 ? '60%' : '100%',
                height: i === 0 ? 20 : 14,
                marginBottom: 4,
            }))}
        />
    </CardSkeletonContainer>
);

interface SkeletonListItemProps {
    hasAvatar?: boolean;
    hasRightContent?: boolean;
}

export const SkeletonListItem: React.FC<SkeletonListItemProps> = () => (
    <GBSkeletonListItem />
);

interface SkeletonListProps {
    count?: number;
    hasAvatar?: boolean;
}

export const SkeletonList: React.FC<SkeletonListProps> = ({ count = 5 }) => (
    <div data-testid="skeleton-list">
        {Array.from({ length: count }).map((_, i) => (
            <GBSkeletonListItem key={i} />
        ))}
    </div>
);

interface SkeletonTableProps {
    columns?: number;
    rows?: number;
}

const TableSkeletonContainer = styled.div`
    background-color: ${props => props.theme.background.paper};
    border: 1px solid ${props => props.theme.border.default};
    border-radius: ${borderRadiusTokens.md};
    overflow: hidden;
`;

const TableHeader = styled.div`
    display: flex;
    padding: ${spacingTokens.md} ${spacingTokens.lg};
    background-color: ${props => props.theme.background.subtle};
    border-bottom: 2px solid ${props => props.theme.border.default};
    gap: ${spacingTokens.lg};
`;

const TableRow = styled.div`
    display: flex;
    padding: ${spacingTokens.md} ${spacingTokens.lg};
    border-bottom: 1px solid ${props => props.theme.border.subtle};
    gap: ${spacingTokens.lg};

    &:last-child {
        border-bottom: none;
    }
`;

const TableCell = styled.div<{ $flex?: number }>`
    flex: ${props => props.$flex || 1};
`;

export const SkeletonTable: React.FC<SkeletonTableProps> = ({ columns = 4, rows = 5 }) => (
    <TableSkeletonContainer data-testid="skeleton-table">
        <TableHeader>
            {Array.from({ length: columns }).map((_, i) => (
                <TableCell key={i} $flex={i === 0 ? 2 : 1}>
                    <GBSkeleton type="text" width="80%" height={14} />
                </TableCell>
            ))}
        </TableHeader>
        {Array.from({ length: rows }).map((_, rowIndex) => (
            <TableRow key={rowIndex}>
                {Array.from({ length: columns }).map((_, colIndex) => (
                    <TableCell key={colIndex} $flex={colIndex === 0 ? 2 : 1}>
                        <GBSkeleton type="text" width={colIndex === 0 ? "70%" : "60%"} height={14} />
                    </TableCell>
                ))}
            </TableRow>
        ))}
    </TableSkeletonContainer>
);

const HeroSkeletonContainer = styled.div`
    background: linear-gradient(135deg,
        ${props => props.theme.background.subtle} 0%,
        ${props => props.theme.border.subtle} 100%
    );
    border-radius: ${borderRadiusTokens.md};
    padding: ${spacingTokens.xxl};
    display: flex;
    flex-direction: column;
    gap: ${spacingTokens.lg};
`;

export const SkeletonHero: React.FC = () => (
    <HeroSkeletonContainer data-testid="skeleton-hero">
        <GBSkeleton type="text" width={120} height={14} />
        <GBSkeleton type="text" width={200} height={40} />
        <GBSkeleton type="text" width={150} height={20} />
    </HeroSkeletonContainer>
);

interface SkeletonChartProps {
    height?: string;
}

export const SkeletonChart: React.FC<SkeletonChartProps> = ({ height = "300px" }) => (
    <GBSkeleton
        type="chart"
        height={parseInt(height)}
        testID="skeleton-chart"
    />
);

const PageSkeletonContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacingTokens.xxl};
    padding: ${spacingTokens.xxl};
`;

interface SkeletonPageProps {
    hasHero?: boolean;
    hasChart?: boolean;
    listCount?: number;
}

export const SkeletonPage: React.FC<SkeletonPageProps> = ({
    hasHero = true,
    hasChart = false,
    listCount = 5
}) => (
    <PageSkeletonContainer data-testid="skeleton-page">
        {hasHero && <SkeletonHero />}
        {hasChart && <SkeletonChart />}
        <SkeletonList count={listCount} />
    </PageSkeletonContainer>
);

// Loading container wrapper with centered state
export const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacingTokens.lg};
    padding: ${spacingTokens.lg};
`;

const Skeleton = {
    SkeletonText,
    SkeletonCircle,
    SkeletonBox,
    SkeletonCard,
    SkeletonListItem,
    SkeletonList,
    SkeletonTable,
    SkeletonHero,
    SkeletonChart,
    SkeletonPage,
    LoadingContainer,
};

export default Skeleton;
